// Taak-expansie voor een venster (ONTWERP.md §5.2 expansie-definitie, §5.4): regel-slots per
// (serie, lid, datum) binnen de assignee-validiteit, met de persistente instance-rijen er met een
// LEFT JOIN overheen. Verplaatste instances verschijnen op hun scheduled_date (move-in) en niet op
// hun original_date (move-out). Pure functie: rijen in, taakitems per dag uit.
import { compareLocalDate } from '../recurrence/localDate.js';
import { displayDates, taskSlotDates } from './schedule.js';
import { aggregateTaskState, deriveTaskState, TASK_UNDO_WINDOW_MS, } from './taskState.js';
const DAYPART_ORDER = { morning: 0, afternoon: 1, evening: 2 };
export function validOn(a, date) {
    return (compareLocalDate(a.valid_from, date) <= 0 &&
        (a.valid_to === null || compareLocalDate(a.valid_to, date) >= 0));
}
/** Effectieve punten van een lid voor een serie op een datum (patch > override > serie). */
export function effectivePoints(series, assignee, patch) {
    const patched = patch?.points;
    if (typeof patched === 'number')
        return patched;
    return assignee?.points_override ?? series.points;
}
function hhmmOf(time) {
    if (time === null)
        return null;
    const m = /^(\d{2}):(\d{2})/.exec(time);
    return m ? `${m[1]}:${m[2]}` : null;
}
function str(v) {
    return typeof v === 'string' ? v : null;
}
/** Items per dag (sleutel = getoonde datum) voor `buildDays`. */
export function expandTasks(input) {
    const out = new Map();
    const seriesById = new Map(input.series.map((s) => [s.id, s]));
    const assigneesBySeries = new Map();
    for (const a of input.assignees) {
        const list = assigneesBySeries.get(a.series_id);
        if (list)
            list.push(a);
        else
            assigneesBySeries.set(a.series_id, [a]);
    }
    // Instances per familie-sleutel (root, lid, original_date): een split houdt de sleutel.
    const instanceByKey = new Map();
    for (const i of input.instances) {
        instanceByKey.set(`${i.root_series_id}|${i.member_id ?? ''}|${i.original_date}`, i);
    }
    // Voor move-in: een serie van de familie (de rij kan onder een oudere split-serie hangen).
    const seriesByRoot = new Map();
    for (const s of input.series) {
        const cur = seriesByRoot.get(s.root_series_id);
        if (!cur || compareLocalDate(s.start_date, cur.start_date) > 0)
            seriesByRoot.set(s.root_series_id, s);
    }
    const participantsByInstance = new Map();
    for (const p of input.participations) {
        const list = participantsByInstance.get(p.task_instance_id);
        if (list)
            list.push(p);
        else
            participantsByInstance.set(p.task_instance_id, [p]);
    }
    const emitted = new Set();
    const push = (date, item) => {
        if (compareLocalDate(date, input.from) < 0 || compareLocalDate(date, input.to) > 0)
            return;
        const list = out.get(date);
        if (list)
            list.push(item);
        else
            out.set(date, [item]);
    };
    const view = (series, memberId, keyDate, row, assignee, validMembers) => {
        const derived = deriveTaskState(row?.state ?? null, keyDate, input.today);
        const participants = row ? (participantsByInstance.get(row.id) ?? []) : [];
        const leftOpen = row !== undefined && (row.state === 'done' || row.state === 'pending');
        let points;
        if (leftOpen && row) {
            points =
                memberId !== null
                    ? (participants.find((p) => p.member_id === memberId)?.points_snapshot ??
                        row.points_snapshot ??
                        0)
                    : (row.points_snapshot ?? 0);
        }
        else if (memberId !== null) {
            points = input.pointsMemberIds.has(memberId)
                ? effectivePoints(series, assignee, row?.patch)
                : 0;
        }
        else {
            points = effectivePoints(series, undefined, row?.patch);
        }
        const completedAt = leftOpen && row ? row.completed_at : null;
        return {
            member_id: memberId,
            state: derived.state,
            points: Math.max(0, points),
            completed_by_member_id: leftOpen && row ? row.completed_by_member_id : null,
            completed_at: completedAt,
            undo_until: completedAt !== null
                ? new Date(new Date(completedAt).getTime() + TASK_UNDO_WINDOW_MS).toISOString()
                : null,
            claimed_by_member_id: row &&
                (derived.state === 'open' || derived.state === 'missed' || derived.state === 'rejected')
                ? row.claimed_by_member_id
                : null,
            late_completable: derived.late_completable,
            rejection_note: derived.state === 'rejected' && row ? row.rejection_note : null,
            participant_member_ids: leftOpen
                ? participants.map((p) => p.member_id)
                : memberId !== null
                    ? [memberId]
                    : [...validMembers],
        };
    };
    const buildItem = (series, keyDate, displayDate, instances, memberIds, patch, moved) => {
        const done = new Set();
        const pending = new Set();
        for (const v of instances) {
            if (v.state === 'done')
                for (const m of v.participant_member_ids)
                    done.add(m);
            if (v.state === 'pending')
                for (const m of v.participant_member_ids)
                    pending.add(m);
        }
        const timeOfDay = str(patch?.time_of_day) ?? series.time_of_day;
        return {
            id: series.id,
            title: str(patch?.title_snapshot) ?? str(patch?.title) ?? series.title,
            member_ids: memberIds,
            icon: patch && 'icon_snapshot' in patch
                ? str(patch.icon_snapshot)
                : patch && 'icon' in patch
                    ? str(patch.icon)
                    : series.icon,
            points: effectivePoints(series, undefined, patch),
            done_member_ids: [...done],
            version: series.version,
            date: displayDate,
            key: `${series.root_series_id}:${keyDate}`,
            original_date: keyDate,
            mode: series.mode,
            time_of_day: timeOfDay === 'morning' || timeOfDay === 'afternoon' || timeOfDay === 'evening'
                ? timeOfDay
                : null,
            due_hhmm: hhmmOf(str(patch?.due_time) ?? series.due_time),
            announce: series.announce === true,
            needs_approval: series.approval_required,
            moved,
            sort_order: series.sort_order,
            state: aggregateTaskState(instances.map((v) => v.state)),
            pending_member_ids: [...pending],
            instances,
        };
    };
    for (const series of input.series) {
        const assignees = assigneesBySeries.get(series.id) ?? [];
        // Meerdaagse eenmalige taak: de sleutel kan buiten het venster vallen terwijl hij erin getoond
        // wordt; daarom het venster voor de sleutels uitbreiden tot de vervaldatum.
        const keyWindowTo = series.rrule === null &&
            series.due_date !== null &&
            compareLocalDate(series.due_date, input.to) > 0
            ? series.due_date
            : input.to;
        const keys = taskSlotDates(series, { from: input.from, to: keyWindowTo });
        for (const keyDate of keys) {
            const shows = displayDates(series, keyDate);
            const valid = assignees.filter((a) => validOn(a, keyDate));
            const validMembers = [...new Set(valid.map((a) => a.member_id))];
            if (series.mode === 'each') {
                const stay = [];
                const stayMembers = [];
                let patch;
                for (const memberId of validMembers) {
                    const row = instanceByKey.get(`${series.root_series_id}|${memberId}|${keyDate}`);
                    if (row && row.scheduled_date !== keyDate)
                        continue; // move-out: elders getoond
                    if (row)
                        emitted.add(row.id);
                    const a = valid.find((x) => x.member_id === memberId);
                    stay.push(view(series, memberId, keyDate, row, a, validMembers));
                    stayMembers.push(memberId);
                    patch = patch ?? row?.patch;
                }
                if (stay.length === 0)
                    continue;
                for (const d of shows)
                    push(d, buildItem(series, keyDate, d, stay, stayMembers, patch, false));
            }
            else {
                if (validMembers.length === 0)
                    continue;
                const row = instanceByKey.get(`${series.root_series_id}||${keyDate}`);
                if (row && row.scheduled_date !== keyDate)
                    continue;
                if (row)
                    emitted.add(row.id);
                const v = view(series, null, keyDate, row, undefined, validMembers);
                for (const d of shows)
                    push(d, buildItem(series, keyDate, d, [v], validMembers, row?.patch, false));
            }
        }
    }
    // Move-in: rijen met scheduled_date ≠ original_date die hierboven niet getoond zijn.
    const movedGroups = new Map();
    for (const i of input.instances) {
        if (emitted.has(i.id) || i.scheduled_date === i.original_date)
            continue;
        if (compareLocalDate(i.scheduled_date, input.from) < 0)
            continue;
        if (compareLocalDate(i.scheduled_date, input.to) > 0)
            continue;
        const series = seriesById.get(i.series_id) ?? seriesByRoot.get(i.root_series_id);
        if (!series)
            continue;
        const k = `${series.root_series_id}|${i.scheduled_date}|${i.original_date}`;
        const list = movedGroups.get(k);
        if (list)
            list.push(i);
        else
            movedGroups.set(k, [i]);
    }
    for (const rows of movedGroups.values()) {
        const first = rows[0];
        if (!first)
            continue;
        const series = seriesById.get(first.series_id) ?? seriesByRoot.get(first.root_series_id);
        if (!series)
            continue;
        const assignees = assigneesBySeries.get(series.id) ?? [];
        const valid = assignees.filter((a) => validOn(a, first.original_date));
        const validMembers = [...new Set(valid.map((a) => a.member_id))];
        const views = rows.map((row) => view(series, row.member_id, 
        // Een verplaatste instance vervalt op zijn nieuwe dag (§5.4: skipped/verplaatst neutraal).
        row.scheduled_date, row, valid.find((a) => a.member_id === row.member_id), validMembers));
        const memberIds = series.mode === 'each'
            ? rows.map((r) => r.member_id).filter((m) => m !== null)
            : validMembers;
        push(first.scheduled_date, buildItem(series, first.original_date, first.scheduled_date, views, memberIds, first.patch, true));
    }
    for (const list of out.values())
        list.sort(compareTaskItems);
    return out;
}
/**
 * Een geplande taak hoort bij zijn eigen dag (fix/taken-per-dag, oprichter 5 okt 2026). Op een dag
 * staan alleen de taken met die sleutel (original_date), of een latere (meerdaags eenmalig: de
 * vervaldatum), of die een ouder naar die dag verplaatste (moved). Wat op een eerdere dag open
 * bleef, komt nooit als tweede exemplaar terug; het blijft als gemiste rij in de geschiedenis
 * (opgeslagen staat ongewijzigd 'open' of geen rij: geen punten, geen audit, geen change_log) en
 * een ouder kan hem in de ouderapp nog afronden. `expandTasks` houdt zich hier al aan; deze regel
 * is de defensieve filter voor de lijsten van vandaag (bord en ouderapp).
 */
export function belongsToDay(task, day) {
    if (task.moved === true || !task.original_date)
        return true;
    return compareLocalDate(task.original_date, day) >= 0;
}
/** Dagdeel (ochtend, middag, avond, zonder), dan ouder-volgorde, dan titel (TSK-11). */
export function compareTaskItems(a, b) {
    const da = a.time_of_day === null ? 3 : (DAYPART_ORDER[a.time_of_day] ?? 3);
    const db = b.time_of_day === null ? 3 : (DAYPART_ORDER[b.time_of_day] ?? 3);
    if (da !== db)
        return da - db;
    if (a.sort_order !== b.sort_order)
        return a.sort_order - b.sort_order;
    return a.title.localeCompare(b.title, 'nl') || a.key.localeCompare(b.key);
}
//# sourceMappingURL=expand.js.map